import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Building2, Handshake, ArrowRight } from 'lucide-react';

const Partnership = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="partnership" className="py-20 md:py-32 px-6 lg:px-12 bg-isk-bg" data-testid="partnership-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{color:'#0B6F63'}}>
            Skema Kerjasama
          </h2>
          <p className="text-lg text-isk-gold/70 max-w-2xl mx-auto">
            Pilih model kerjasama yang sesuai dengan kebutuhan investasi properti Anda
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Kelola Kost Existing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-2xl p-8 md:p-12 hover:-translate-y-1 h-full flex flex-col"
            data-testid="partnership-card-0"
          >
            <div className="w-16 h-16 rounded-2xl bg-isk-teal/10 flex items-center justify-center mb-6">
              <Building2 className="w-8 h-8 text-isk-teal" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{color:'#0B6F63'}}>
              Kelola Kost Existing
            </h3>

            <p className="text-isk-muted mb-8">
              Sudah punya properti kost? Kami kelola operasional lengkap untuk maksimalkan occupancy dan ROI Anda.
            </p>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-teal/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-teal"></div>
                </div>
                <span style={{color:'#0B6F63'}}>Full operational management</span>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-teal/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-teal"></div>
                </div>
                <span style={{color:'#0B6F63'}}>Marketing & tenant acquisition</span>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-teal/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-teal"></div>
                </div>
                <span style={{color:'#0B6F63'}}>Maintenance & housekeeping</span>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-teal/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-teal"></div>
                </div>
                <span style={{color:'#0B6F63'}}>Financial reporting</span>
              </li>
            </ul>

            <Button
              onClick={() => scrollToSection('contact')}
              className="w-full mt-auto bg-isk-teal hover:bg-isk-dark text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all"
              data-testid="partnership-cta-0"
            >
              Ajukan Sekarang
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>

          {/* Bangun di Tanah */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-card rounded-2xl p-8 md:p-12 hover:-translate-y-1 h-full flex flex-col"
            data-testid="partnership-card-1"
          >
            <div className="w-16 h-16 rounded-2xl bg-isk-gold/10 flex items-center justify-center mb-6">
              <Handshake className="w-8 h-8 text-isk-gold" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-isk-gold mb-4">
              Bangun di Tanah (Profit Sharing)
            </h3>

            <p className="text-isk-muted mb-8">
              Punya tanah strategis? Kami bangun dan kelola properti kost premium dengan skema profit sharing.
            </p>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-gold/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-gold"></div>
                </div>
                <span className="text-isk-gold">Zero investment dari owner</span>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-gold/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-gold"></div>
                </div>
                <span className="text-isk-gold">Professional property development</span>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-gold/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-gold"></div>
                </div>
                <span className="text-isk-gold">Profit sharing arrangement</span>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-isk-gold/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-isk-gold"></div>
                </div>
                <span className="text-isk-gold">Long-term partnership</span>
              </li>
            </ul>

            <Button
              onClick={() => scrollToSection('contact')}
              className="w-full mt-auto bg-isk-gold hover:bg-isk-gold-hover text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all"
              data-testid="partnership-cta-1"
            >
              Diskusi Skema
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Partnership;
